import React, { useEffect, useRef, useState } from "react";
// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";
import Spinner from "react-bootstrap/Spinner";
import { unbxdCusEngTrackEvent, unbxdCusEngTrackUser } from "@/lib/unbxd";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import SwiperCore, {
  FreeMode,
  Navigation,
  Pagination,
  Autoplay,
  Thumbs,
} from "swiper";
import type { NextPage } from "next";
import Image from "next/image";
import Layout from "@/layout/layout";

import horoscopebanner from "@/assets/images/ast-banner.jpg";
import gline from "@/assets/images/line-gray.png";
import styles from "@/styles/astrokundali.module.scss";
import * as Yup from "yup";
import { phoneRegExp } from "@/utils/regex";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import { useRouter } from "next/router";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { authActions, selectAuth } from "@/store/slices/auth";
import { useSelector } from "react-redux";
import { useAppDispatch, wrapper } from "@/store";
import CurrencyInr from "@/components/common/currency-inr";
import { setPath, setTitle, setMeta } from "@/store/slices/helmet";

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const AstroKundali: NextPage = () => {
  const router = useRouter();
  const authState = useSelector(selectAuth);
  const dispatch = useAppDispatch();
  const [state, setState] = useState<any>([]);

  const [loading, setLoading] = useState(false);

  const [data, setData] = useState<any>(null);
  const [kundaliTypes, setKundaliTypes] = useState<any>([]);
  const [type, setType] = useState<any>(null);
  const formik = useFormik({
    initialValues: {
      type_uuid: "",
      name: "",
      email: "",
      mobile: "",
      gender: "", // male, female, other
      place_of_birth: "",
      date_of_birth: "",
      time_of_birth: "",
      state: "",
      address: "",
    },
    validationSchema: Yup.object({
      state: Yup.string().required("State is required"),
      address: Yup.string().required("Address is required"),
      type_uuid: Yup.string().required("Type is required"),
      date_of_birth: Yup.date()
        .required("Date of birth is required")
        .max(new Date(), "Date cannot be in the future"),
      time_of_birth: Yup.string().required("Time of birth is required"),

      name: Yup.string()
        .required("Name is required")
        .matches(/^[a-zA-Z\s]+$/, "Invalid name format"),

      gender: Yup.string().required("Gender is required"),
      email: Yup.string()
        .email("Invalid email format")
        .required(" Email is Required"),
      place_of_birth: Yup.string().required("Place of birth is required"),
      mobile: Yup.string()
        .matches(phoneRegExp, "Mobile number is not valid")
        .test(
          "len",
          "Mobile number needs to be exactly 10 digits",
          (val: any) => val?.toString().length === 10
        )
        .required("Mobile is required"),
    }),
    onSubmit: async (values) => {
      if (!authState.user) {
        dispatch(authActions.setIsModalOpen(true));
        return;
      }

      setLoading(true);
      try {
        const { data } = await httpClient.post(
          endpoints.astro.bookKundaliv2(),
          {
            ...values,
            time_of_birth: values.time_of_birth + ":00",
          }
        );
        if (window.smartech) {
          unbxdCusEngTrackEvent("kundli_form_filled", {
            type: kundaliTypes.find((v: any) => v.uuid == values.type_uuid)
              ?.name,
            amount: kundaliTypes.find((v: any) => v.uuid == values.type_uuid)
              ?.amount,
          });
          window.smartech(
            "identify",
            `${process.env.NEXT_PUBLIC_MOB_PREFIX}${values.mobile}`
          );
          unbxdCusEngTrackUser({
            "pk^mobile": `${process.env.NEXT_PUBLIC_MOB_PREFIX}${values.mobile}`,
            email: values.email,
            NAME: values.name,
            GENDER: values.gender,
            SENCO_USER: "yes",
          });
        }
        console.log("data", data);
        if (data && data.params) {
          const scriptLoaded = await loadRazorpayScript();
          if (!scriptLoaded || !window.Razorpay) {
            toast.error("Razorpay SDK failed to load");
            return;
          }
          const options = {
            key: data.params.key,
            amount: data.params.amount,
            currency: data.params.currency,
            name: data.params.name,
            description: data.params.description,
            image: data.params.image,
            order_id: data.params.order_id,
            callback_url: data.params.callback_url,
            prefill: {
              name: data.params.prefill.name,
              email: data.params.prefill.email,
              contact: data.params.prefill.contact,
            },
            notes: data.params.notes,
            theme: {
              color: "#3399cc",
            },
            handler: function (response: any) {
              console.log(response);
              router.push(`/astro/kundali/status/${data.txnid}`);
            },
            modal: {
              ondismiss: async () => {
                router.push(`/astro/kundali/status/${data.txnid}`);
              },
            },
          };
          if (window.Razorpay) {
            const rzp1 = new window.Razorpay(options);
            rzp1.open();
          } else {
            setLoading(false);
            toast("Razorpay SDK not available", { type: "error" });
          }
        } else {
          window.location.href = data.redirect;
        }
      } catch (error: any) {
        toast(error?.response?.data?.message || "Something Went wrong", {
          type: "warning",
        });
      }

      setLoading(false);
    },
  });
  const loadDetails = async () => {
    try {
      const { data } = await httpClient.get(endpoints.astro.kundaliTypes());
      setKundaliTypes(data);
      formik.setFieldValue("type_uuid", data[0].uuid);
    } catch (error) {}
  };

  useEffect(() => {
    loadDetails();
    stateLoad("IND");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stateLoad = async (country: any) => {
    const { data } = await httpClient.get(endpoints.state(country));
    setState(data.data);
  };

  return (
    <Layout>
      <div className={styles.astro_banner}>
        <Image src={horoscopebanner} alt="" />
        <div className={styles.astro_banner_text}>
          <div className="container h_100">
            <div className={styles.astro_banner_main}>
              <div className={styles.astro_banner_main_text}>
                <h1>Life Kushti and Kundli Matching</h1>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.astrokundali_bg}>
        <div className="container">
          <div className={styles.astrokundali_bg_main}>
            <div className={styles.heading}>
              GET FUTURE PREDICTIONS WITH <strong>JANAM KUNDLI</strong>
            </div>
            <div className={styles.gline}>
              <Image src={gline} alt="" />
            </div>
            <div className="row justify-content-center mt-4 mb-4">
              <div className="col-md-12">
                <p className="text-center">
                  Kundli is a success tool of life. Share with us the exact date
                  and time of your birth and we will prepare your Janam Kundli
                  (Life Kushti). An astrological chart representing the position
                  of the Sun, Moon and other celestial bodies at the time of
                  your birth. You can also choose from various types of Kundli
                  services according to your specifications.
                </p>
                <p className="text-center">
                  Our customized <strong>Kundli Matching</strong> services will
                  help in determining your compatibility with your would-be
                  spouse. You will get an in-depth analysis depending on the
                  position of the planets in one’s horoscope. You can gain a
                  deeper understanding of yourself, take informed decisions
                  related to your financial career or navigate your
                  relationship. Senco Gold & Diamonds has carved a niche in the
                  industry by providing detailed Kundlis that give a better
                  understanding of different life factors.
                </p>
              </div>
            </div>
            {kundaliTypes && (
              <>
                <form onSubmit={formik.handleSubmit}>
                  <div className="row">
                    <div className="col-md-12">
                      <div className={styles.form_container}>
                        <div className={styles.heading_main_form}>
                          ENTER DETAILS
                        </div>
                        <div className="row check">
                          <div className="col-md-12">
                            <label>
                              Type Of Kundli{" "}
                              {formik.touched.type_uuid &&
                              formik.errors.type_uuid ? (
                                <small className="text-danger">
                                  {formik.errors.type_uuid}
                                </small>
                              ) : null}
                            </label>
                          </div>
                          {!!kundaliTypes.length &&
                            kundaliTypes.map((v: any) => (
                              <>
                                <div className="col-md-4">
                                  <div className={styles.radio_card}>
                                    <div className="row">
                                      <div className="col-md-6">
                                        <div className="form-check">
                                          <input
                                            className="form-check-input"
                                            type="radio"
                                            checked={
                                              formik.values.type_uuid == v.uuid
                                            }
                                            onChange={(e) => {
                                              formik.setFieldValue(
                                                "type_uuid",
                                                v.uuid
                                              );
                                            }}
                                          />
                                          <label className="form-check-label">
                                            {v.name}
                                          </label>
                                        </div>
                                      </div>
                                      <div className="col-md-6 text-md-end">
                                        <label className="ps-4 ps-md-0">
                                          <strong>
                                            {" "}
                                            <CurrencyInr value={v.amount} />
                                          </strong>
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ))}
                          <div className="col-md-12">
                            <ul className={styles.text_list}>
                              {kundaliTypes
                                .find(
                                  (x: any) => x.uuid == formik.values.type_uuid
                                )
                                ?.info?.split(",")
                                ?.map((type: any) => (
                                  <>
                                    <li>{type}</li>
                                  </>
                                ))}
                            </ul>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label
                                htmlFor="exampleFormControlInput1"
                                className="form-label"
                              >
                                Name
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="exampleFormControlInput1"
                                placeholder="Name"
                                {...formik.getFieldProps("name")}
                              />
                              {formik.touched.name && formik.errors.name ? (
                                <small className="text-danger">
                                  {formik.errors.name}
                                </small>
                              ) : null}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label
                                htmlFor="exampleFormControlInput1"
                                className="form-label"
                              >
                                Email
                              </label>
                              <input
                                type="email"
                                className="form-control"
                                id="exampleFormControlInput1"
                                placeholder="Email"
                                {...formik.getFieldProps("email")}
                              />
                              {formik.touched.email && formik.errors.email ? (
                                <small className="text-danger">
                                  {formik.errors.email}
                                </small>
                              ) : null}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label
                                htmlFor="exampleFormControlInput1"
                                className="form-label"
                              >
                                Mobile Number
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder=""
                                {...formik.getFieldProps("mobile")}
                              />
                              {formik.touched.mobile && formik.errors.mobile ? (
                                <small className="text-danger">
                                  {formik.errors.mobile}
                                </small>
                              ) : null}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label
                                htmlFor="exampleFormControlInput1"
                                className="form-label"
                              >
                                Gender
                              </label>
                              <select
                                className="form-select"
                                {...formik.getFieldProps("gender")}
                              >
                                <option value="">Select</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                              </select>
                              {formik.touched.gender && formik.errors.gender ? (
                                <small className="text-danger">
                                  {formik.errors.gender}
                                </small>
                              ) : null}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label
                                htmlFor="exampleFormControlInput1"
                                className="form-label"
                              >
                                Place of Birth
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="exampleFormControlInput1"
                                placeholder=""
                                {...formik.getFieldProps("place_of_birth")}
                              />
                              {formik.touched.place_of_birth &&
                              formik.errors.place_of_birth ? (
                                <small className="text-danger">
                                  {formik.errors.place_of_birth}
                                </small>
                              ) : null}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label
                                htmlFor="exampleFormControlInput1"
                                className="form-label"
                              >
                                Date of Birth
                              </label>
                              <input
                                type="date"
                                className="form-control"
                                id="exampleFormControlInput1"
                                placeholder=""
                                {...formik.getFieldProps("date_of_birth")}
                              />
                              {formik.touched.date_of_birth &&
                              formik.errors.date_of_birth ? (
                                <small className="text-danger">
                                  {formik.errors.date_of_birth}
                                </small>
                              ) : null}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label
                                htmlFor="exampleFormControlInput1"
                                className="form-label"
                              >
                                Time of Birth
                              </label>
                              <input
                                type="time"
                                className="form-control"
                                id="timeofbirth"
                                placeholder=""
                                {...formik.getFieldProps("time_of_birth")}
                              />
                              {formik.touched.time_of_birth &&
                              formik.errors.time_of_birth ? (
                                <small className="text-danger">
                                  {formik.errors.time_of_birth}
                                </small>
                              ) : null}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label
                                htmlFor="exampleFormControlInput1"
                                className="form-label"
                              >
                                State / Province{" "}
                              </label>
                              <select
                                className="form-select"
                                {...formik.getFieldProps("state")}
                              >
                                <option value="">Select</option>
                                {!!state.length &&
                                  state.map((v: any) => (
                                    <option value={v.code} key={v.code}>
                                      {v.name}
                                    </option>
                                  ))}
                              </select>
                              {formik.touched.state && formik.errors.state ? (
                                <small className="text-danger">
                                  {formik.errors.state}
                                </small>
                              ) : null}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label
                                htmlFor="exampleFormControlInput1"
                                className="form-label"
                              >
                                Address{" "}
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder=""
                                {...formik.getFieldProps("address")}
                              />
                              {formik.touched.address &&
                              formik.errors.address ? (
                                <small className="text-danger">
                                  {formik.errors.address}
                                </small>
                              ) : null}
                            </div>
                          </div>
                          <div className="col-md-9"></div>
                          <div className="col-md-3 text-md-end">
                            <button
                              aria-label="Buy Now"
                              className={styles.appoint}
                              disabled={loading}
                            >
                              Buy Now{" "}
                              {loading && (
                                <Spinner animation="border" size="sm" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>{" "}
                </form>
                {/* <div className={styles.card_border_new}>
                  <div className={styles.card_head_new}>Payment Deails</div>
                  <div className={styles.line_head}></div>
                  <div className="row justify-content-center">
                    <div className="col-md-7 col-lg-5 mb-4">
                      <div className={styles.coupon_apply}>
                        <input
                          type="text"
                          className="form-control"
                          placeholder=" Enter coupon code"
                          name="coupon"
                          value=""
                        />
                        <button>Apply</button>
                      </div>
                      <div className="d-none">
                        <div className={styles.coupon_apply}>
                          <input
                            type="text"
                            className="form-control"
                            placeholder=" Enter coupon code"
                            name="coupon"
                            value=""
                          />
                          <div className={styles.w_166}>
                            <button className={styles.button_cross}>
                              <i className="bi bi-x-lg"></i>
                            </button>
                            <button>Apply</button>
                          </div>
                        </div>
                      </div>
                      <div className="d-none">
                        <div className={styles.coupon_apply}>
                          <input
                            type="text"
                            className="form-control"
                            placeholder=" Enter OTP"
                            name="coupon"
                            value=""
                          />
                          <button>Apply</button>
                        </div>
                        <a className={styles.otp_resend}>Resend OTP</a>
                      </div>
                    </div>
                  </div>
                  <div className="row justify-content-center">
                    <div className="col-md-7 col-lg-5">
                      <table>
                        <tr>
                          <td>Subtotal Amount : </td>
                          <td className="text-end">
                            <strong>₹ 2000</strong>
                          </td>
                        </tr>
                        <tr>
                          <td>You Save : </td>
                          <td className="text-end">
                            <strong>₹ 500 </strong>
                          </td>
                        </tr>
                        <tr>
                          <td>Total Payble Amount : </td>
                          <td className="text-end">
                            <strong>₹ 2000</strong>
                          </td>
                        </tr>
                      </table>
                    </div>
                  </div>
                  <button
                    aria-label="Buy Now"
                    className={styles.button}
                    disabled={loading}
                  >
                    Buy Now{" "}
                    {loading && <Spinner animation="border" size="sm" />}
                  </button>
                </div> */}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ resolvedUrl }) => {
      store.dispatch(setPath(resolvedUrl));
      store.dispatch(setTitle("Check Janam Kundali Online | Senco Gold"));
      store.dispatch(
        setMeta({
          title: "Check Janam Kundali Online | Senco Gold",
          description:
            "Discover elegance at its finest with Senco Gold's best jewellery store near you. Explore exquisite designs crafted for every occasion.",
          keywords: "Check Janam Kundali Online | Senco Gold",
        })
      );

      return {
        props: {},
      };
    }
);

export default AstroKundali;
